/**
 * NeuralAvatarStream
 * ==================
 * Two-state avatar for the AI Agent call hub (CPU-only sprite pipeline —
 * see Docs/NEURAL_AVATAR_CPU_FRIENDLY_LIPSYNC_OPTIONS.md; MuseTalk's idle
 * MP4 loop and live per-reply Wav2Lip video generation are both gone):
 *
 *   inactive (active=false)
 *     → AIAvatar SVG in STANDBY mode — zero network activity.
 *
 *   active (active=true), sprites baked
 *     → SpriteAvatar: swaps between 18 pre-baked static images (9 visemes x
 *       open/closed eyes), timed by a real audio-derived viseme timeline
 *       from Rhubarb Lip Sync over /ws/neural-avatar. Handles both idle
 *       (rest-pose + blink) and talking (viseme swap) itself — no separate
 *       agentTalking branch needed at this level anymore.
 *
 *   active, no sprites baked yet (never ran /avatar/generate-preview, or the
 *   bake failed)
 *     → Falls back to AIAvatar (with agentTalking forwarded so it still
 *       animates), same fallback philosophy as before.
 *
 * Ref handle (NeuralAvatarHandle) is forwarded so the parent can call
 * sendText() / sendPcm() to push audio/text to the backend.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { API_BASE_URL } from '../../config/api';

import { AIAvatar } from './AIAvatar';
import { SpriteAvatar } from './SpriteAvatar';
import type { NeuralAvatarHandle, NeuralAvatarProps } from './avatarTypes';

// ─── Constants ────────────────────────────────────────────────────────────────

const SPRITES_URL = `${API_BASE_URL}/avatar/sprites`;

// ─── Component ────────────────────────────────────────────────────────────────

export const NeuralAvatarStream = forwardRef<NeuralAvatarHandle, NeuralAvatarProps>(
  ({ active, width, height, agentTalking = false }, ref) => {

    // Whether a baked sprite set exists (404 → fall back to SVG)
    const [hasSprites, setHasSprites] = useState<boolean | null>(null); // null = unchecked

    const spriteRef = useRef<NeuralAvatarHandle>(null);
    const aiRef     = useRef<NeuralAvatarHandle>(null);

    useImperativeHandle(ref, () => ({
      sendPcm(pcm: ArrayBuffer) {
        spriteRef.current?.sendPcm(pcm);
        aiRef.current?.sendPcm(pcm);
      },
      sendText(text: string) {
        spriteRef.current?.sendText(text);
        aiRef.current?.sendText(text);
      },
      getAudioStream(): MediaStream | null {
        return spriteRef.current?.getAudioStream() ?? aiRef.current?.getAudioStream() ?? null;
      },
    }));

    // Probe the sprite manifest once when we become active
    useEffect(() => {
      if (!active || hasSprites !== null) return;
      fetch(SPRITES_URL)
        .then(r => setHasSprites(r.ok))
        .catch(() => setHasSprites(false));
    }, [active, hasSprites]);

    // Reset probe when deactivated so a newly baked sprite set is detected next time
    useEffect(() => {
      if (!active) setHasSprites(null);
    }, [active]);

    // ── Inactive: SVG standby ─────────────────────────────────────────────────
    if (!active) {
      return (
        <AIAvatar
          ref={aiRef}
          active={false}
          width={width}
          height={height}
          agentTalking={false}
        />
      );
    }

    // ── Active + sprites baked: sprite avatar (handles idle + talking itself) ──
    if (hasSprites) {
      return (
        <SpriteAvatar
          ref={spriteRef}
          active={true}
          width={width}
          height={height}
          agentTalking={agentTalking}
        />
      );
    }

    // Fallback: animated SVG (no sprites baked yet, or the manifest fetch failed)
    return (
      <AIAvatar
        ref={aiRef}
        active={true}
        width={width}
        height={height}
        agentTalking={agentTalking}
      />
    );
  },
);

NeuralAvatarStream.displayName = 'NeuralAvatarStream';
